import React, { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { prefetchYears } from "../../services/YearsService";
import { prefetchHolidays } from "../../services/HolidaysService";
import { performanceMonitor } from "../../utils/PerformanceMonitor";

const ResourcePreloader: React.FC = () => {
  const queryClient = useQueryClient();

  useEffect(() => {
    const preloadResources = async () => {
      // 1. Add DNS prefetch for external API
      const dnsApiLink = document.createElement("link");
      dnsApiLink.rel = "dns-prefetch";
      dnsApiLink.href = "https://scsbiz.wearevisum.com";
      document.head.appendChild(dnsApiLink);

      // 2. Add DNS prefetch for Shopify CDN
      const dnsShopifyLink = document.createElement("link");
      dnsShopifyLink.rel = "dns-prefetch";
      dnsShopifyLink.href = "https://cdn.shopify.com";
      document.head.appendChild(dnsShopifyLink);

      // 3. Add preconnect for faster connection establishment - API
      const preconnectApiLink = document.createElement("link");
      preconnectApiLink.rel = "preconnect";
      preconnectApiLink.href = "https://scsbiz.wearevisum.com";
      preconnectApiLink.setAttribute("crossorigin", "anonymous");
      document.head.appendChild(preconnectApiLink);

      // 4. Add preconnect for Shopify CDN
      const preconnectShopifyLink = document.createElement("link");
      preconnectShopifyLink.rel = "preconnect";
      preconnectShopifyLink.href = "https://cdn.shopify.com";
      preconnectShopifyLink.setAttribute("crossorigin", "anonymous");
      document.head.appendChild(preconnectShopifyLink);

      // 5. Add resource hints for Shopify image optimization
      const resourceHintLink = document.createElement("link");
      resourceHintLink.rel = "preload";
      resourceHintLink.as = "image";
      resourceHintLink.type = "image/jpeg";
      document.head.appendChild(resourceHintLink);

      try {
        await prefetchYears(queryClient);
      } catch (error) {
        console.warn("⚠️ Failed to preload years data:", error);
      }

      try {
        await prefetchHolidays(queryClient);
      } catch (error) {
        console.warn("⚠️ Failed to preload holidays data:", error);
      }

      let performanceTimeout: ReturnType<typeof setTimeout> | undefined;
      if (process.env.NODE_ENV === 'development') {
        performanceTimeout = setTimeout(() => {
          performanceMonitor.sendToAnalytics();
        }, 5000);
      }

      // Cleanup function
      return () => {
        dnsApiLink.remove();
        dnsShopifyLink.remove();
        preconnectApiLink.remove();
        preconnectShopifyLink.remove();
        resourceHintLink.remove();
        if (performanceTimeout) {
          clearTimeout(performanceTimeout);
        }
        performanceMonitor.stopMonitoring();
      };
    };

    preloadResources();
  }, [queryClient]);

  return null;
};

export default ResourcePreloader;
